/**
 * Le promesse del volo, sulla scheda della commessa.
 *
 * Il ciclo di vita contava — 78 fotogrammi, 78 con coordinate — e non diceva
 * niente del volo: ne' la quota sui sostegni, ne' il dettaglio a terra, ne'
 * la densita' della nuvola, benche' la consegna li misuri tutti contro cio'
 * che il committente ha promesso a ENEL. Qui si leggono le stesse soglie
 * prima di spendere un turno su un volo che la consegna respingerebbe.
 * Ogni riga: valore, banda o limite, stato a parole e con un tono, origine
 * del limite; «non misurato» resta tale e non diventa uno zero (GI-249).
 */
import { useEffect, useState } from 'react'

import { ServiceError } from './api'
import { StatusPill, type PillTone } from './carta/StatusPill'
import { decimal, notMeasured } from './format'
import { useT } from './i18n/LocaleProvider'
import type { MessageKey } from './i18n/messages'
import { flight as readFlight } from './inspections'
import type { Flight, FlightMeasure } from './types'

const NAME: Record<string, MessageKey> = {
  metadata: 'flight.metadata',
  altitude: 'flight.altitude',
  gsd: 'flight.gsd',
  gsd_promised: 'flight.gsdPromised',
  swath: 'flight.swath',
  density: 'flight.density',
  cir: 'flight.cir',
  wsr: 'flight.wsr',
  sir: 'flight.sir',
  exposure: 'flight.exposure',
  focus: 'flight.focus',
}

const BASIS: Record<string, MessageKey> = {
  norm: 'flight.basisNorm',
  contract: 'flight.basisContract',
  hypothesis: 'flight.basisHypothesis',
}

type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string

/** Il numero con la sua unita', come lo si scrive in un rapporto. */
export function measureText(value: number, unit: string): string {
  if (unit === 'ratio') return `${decimal(value * 100)} %`
  if (unit === 'percent') return `${decimal(value, 1)} %`
  if (unit === 'mm') return `${decimal(value, 1)} mm`
  if (unit === 'm') return `${decimal(value, 1)} m`
  if (unit === 'pt_m2') return `${decimal(value)} pt/m²`
  return decimal(value, 2)
}

/** La banda o il limite: «20–40 m», «≤ 2 mm», «≥ 95 %». */
export function boundText(one: FlightMeasure): string {
  if (one.floor !== null) return `${measureText(one.floor, one.unit).replace(/ .*$/, '')}–${measureText(one.limit, one.unit)}`
  // Le quote e le percentuali di continuita' sono tetti; le altre — metadati,
  // esposizione, fuoco — sono pavimenti.
  const ceiling = one.unit === 'mm' || one.unit === 'percent' || one.unit === 'm'
  return `${ceiling ? '≤' : '≥'} ${measureText(one.limit, one.unit)}`
}

function verdict(one: FlightMeasure, t: Translate): { label: string; tone: PillTone } {
  if (one.measured === null) return { label: t('flight.unmeasured'), tone: 'ink-3' }
  if (one.passed) return { label: t('flight.within'), tone: 'sev-0' }
  return { label: one.blocks ? t('flight.outsideBlocks') : t('flight.outside'), tone: one.blocks ? 'sev-4' : 'sev-3' }
}

export function FlightPromises({ inspectionId, compact = false }: { inspectionId: string; compact?: boolean }): JSX.Element | null {
  const t = useT()
  const [state, setState] = useState<Flight | null>(null)
  const [failure, setFailure] = useState<ServiceError | null>(null)

  useEffect(() => {
    let alive = true
    readFlight(inspectionId)
      .then((found) => alive && setState(found))
      .catch((error: unknown) => {
        if (alive && error instanceof ServiceError) setFailure(error)
      })
    return () => {
      alive = false
    }
  }, [inspectionId])

  if (failure !== null) return null
  if (state === null) return null

  return (
    <section className="volo" aria-label={compact ? undefined : t('flight.heading')}>
      {!compact && <h2>{t('flight.heading')}</h2>}
      {!compact && (
        <p className="tenue">
          {t('flight.note')}
          {state.sampled > 0 && <> · {t('flight.sampled', { n: decimal(state.sampled), of: decimal(state.photos) })}</>}
        </p>
      )}
      <table className="volo-tabella">
        <thead>
          <tr>
            <th scope="col">{t('flight.colPromise')}</th>
            <th scope="col" className="t-num">
              {t('flight.colMeasured')}
            </th>
            {!compact && (
              <th scope="col" className="t-num">
                {t('flight.colBound')}
              </th>
            )}
            <th scope="col">{t('flight.colState')}</th>
            {!compact && <th scope="col">{t('flight.colBasis')}</th>}
          </tr>
        </thead>
        <tbody>
          {state.measures.map((one) => {
            const said = verdict(one, t)
            const nameKey = NAME[one.id]
            const basisKey = BASIS[one.basis]
            return (
              <tr
                key={one.id}
                // In colonna stretta limite e origine stanno qui, nel titolo
                // della riga: si leggono al passaggio, non occupano colonne.
                title={compact ? `${boundText(one)} · ${basisKey === undefined ? one.basis : t(basisKey)}${one.note ? ` · ${one.note}` : ''}` : one.note}
              >
                <th scope="row">{nameKey === undefined ? one.name : t(nameKey)}</th>
                <td className="t-num">{one.measured === null ? notMeasured() : measureText(one.measured, one.unit)}</td>
                {!compact && <td className="t-num tenue">{boundText(one)}</td>}
                <td>
                  <StatusPill label={said.label} tone={said.tone} />
                </td>
                {!compact && <td className="tenue">{basisKey === undefined ? one.basis : t(basisKey)}</td>}
              </tr>
            )
          })}
        </tbody>
      </table>
    </section>
  )
}
